import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spiner } from '../../components';

export type RenameDataCollectionModalProps = {
    collectionId: string;
    initialName: string;
    modalDomId?: string;
    autoShow?: boolean;
    onHidden?: () => void;
    onSave: (trimmedName: string) => Promise<boolean>;
};

const RenameDataCollectionModal: React.FC<RenameDataCollectionModalProps> = ({
    collectionId,
    initialName,
    modalDomId,
    autoShow,
    onHidden,
    onSave,
}) => {
    const { t } = useTranslation();
    const resolvedModalDomId = modalDomId ?? `renameDataCollection${collectionId}`;
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const onHiddenRef = useRef(onHidden);
    onHiddenRef.current = onHidden;
    const [name, setName] = useState(initialName);
    const [isSubmit, setIsSubmit] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setName(initialName);
        setError(null);
    }, [initialName, collectionId]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (!modalElement) return;
        const handleHidden = () => onHiddenRef.current?.();
        modalElement.addEventListener('hidden.bs.modal', handleHidden);
        return () => modalElement.removeEventListener('hidden.bs.modal', handleHidden);
    }, []);

    useEffect(() => {
        if (!autoShow) return;
        const el = modalRef.current;
        if (!el) return;
        const Modal = (window as any).bootstrap?.Modal;
        if (!Modal) return;
        const inst = Modal.getOrCreateInstance(el, { backdrop: 'static' });
        const raf = requestAnimationFrame(() => inst.show());
        return () => {
            cancelAnimationFrame(raf);
            const existing = Modal.getInstance(el);
            if (existing) {
                try {
                    existing.hide();
                } catch {
                    /* ignore */
                }
                existing.dispose();
            }
        };
    }, [autoShow]);

    const closeModal = () => {
        const el = modalRef.current;
        if (!el) return;
        (window as any).bootstrap?.Modal?.getInstance(el)?.hide();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) {
            setError(t('words.error'));
            return;
        }
        if (trimmed === initialName.trim()) {
            closeModal();
            return;
        }
        setIsSubmit(true);
        setError(null);
        try {
            const ok = await onSave(trimmed);
            if (ok) closeModal();
        } finally {
            setIsSubmit(false);
        }
    };

    return (
        <div ref={modalRef} className="modal" data-bs-backdrop="static" id={resolvedModalDomId} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
                <form className="modal-content bg-dark" onSubmit={(e) => void handleSubmit(e)}>
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">{t('words.rename')}</h5>
                        <button type="button" className="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        <label className="form-label text-theme small" htmlFor={`${resolvedModalDomId}-name`}>
                            {t('form.collectWorkspace.renameDataCollectionPrompt', { defaultValue: 'New name' })}
                        </label>
                        <input
                            id={`${resolvedModalDomId}-name`}
                            type="text"
                            className="form-control bg-dark text-white border-secondary"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            maxLength={200}
                        />
                        {error ? (
                            <p className="text-danger small mt-2 mb-0" role="alert">
                                {error}
                            </p>
                        ) : null}
                    </div>
                    <div className="modal-footer">
                        <button ref={closeRef} type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                            {t('words.close')}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmit}>
                            {isSubmit ? <Spiner className="spinner-border-sm" /> : t('words.rename')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RenameDataCollectionModal;
